"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.CodeBlock = void 0;
const tslib_1 = require("tslib");
const React = tslib_1.__importStar(require("react"));
const nano_theme_1 = require("nano-theme");
const slate_react_1 = require("slate-react");
const BasicButtonMore_1 = require("@jsonjoy.com/ui/lib/2-inline-block/BasicButton/BasicButtonMore");
const CopyButton_1 = require("@jsonjoy.com/ui/lib/2-inline-block/CopyButton");
const Popup_1 = require("@jsonjoy.com/ui/lib/4-card/Popup");
const context_1 = require("@jsonjoy.com/ui/lib/styles/context");
const Paper_1 = tslib_1.__importDefault(require("@jsonjoy.com/ui/lib/4-card/Paper"));
const code_colors_react_1 = require("code-colors-react");
const Label_1 = require("@jsonjoy.com/ui/lib/1-inline/Label");
const Iconista_1 = require("@jsonjoy.com/ui/lib/icons/Iconista");
const ui_1 = require("@jsonjoy.com/ui");
const CodeBlockOptionsPopup_1 = require("./CodeBlockOptionsPopup");
const settings = tslib_1.__importStar(require("./settings"));
const CODE_LINE_HEIGHT = 1.7;
const SHARED_TEXT_CSS = {
    ff: '"JetBrains Mono", "Fira Code", Menlo, monospace',
    fz: '0.9rem',
    lh: `${CODE_LINE_HEIGHT}em`,
    pd: '16px 18px 18px 14px',
    mr: 0,
    letterSpacing: '0',
    tabSize: 4,
    fontKerning: 'none',
    fontVariantLigatures: 'none',
    fontFeatureSettings: 'normal',
};
const codeWrapClass = (0, nano_theme_1.rule)({
    pos: 'relative',
    mr: '12px 0',
    ff: '"JetBrains Mono", "Fira Code", Menlo, monospace',
    containerType: 'scroll-state',
});
const stickyHeaderClass = (0, nano_theme_1.rule)({
    pos: 'sticky',
    t: 0,
    z: 1,
    containerType: 'scroll-state',
});
const metaBarClass = (0, nano_theme_1.rule)({
    d: 'flex',
    ai: 'center',
    jc: 'space-between',
    gap: '12px',
    pd: '8px 14px',
    fw: 'wrap',
    us: 'none',
    bdrad: '16px 16px 0 0',
    trs: 'border-bottom-left-radius .3s, border-bottom-right-radius .3s',
    '@container scroll-state(stuck: top)': {
        '&': {
            bdrad: '0 0 16px 16px',
        },
    },
});
const metaInputsClass = (0, nano_theme_1.rule)({
    d: 'flex',
    ai: 'center',
    gap: '10px',
    fw: 'wrap',
    w: '100%',
});
const metaPreviewClass = (0, nano_theme_1.rule)({
    d: 'inline-flex',
    ai: 'center',
    gap: '10px',
    fw: 'wrap',
    minW: '0',
});
const metaActionsClass = (0, nano_theme_1.rule)({
    d: 'inline-flex',
    ai: 'center',
    gap: '8px',
    marginInlineStart: 'auto',
});
const metaLabelClass = (0, nano_theme_1.rule)({
    d: 'inline-flex',
    ai: 'center',
    gap: '8px',
    fz: '12px',
    minW: '0',
});
const metaChipClass = (0, nano_theme_1.rule)({
    fz: '12px',
});
const codePreClass = (0, nano_theme_1.rule)({
    mr: '0',
    w: '100%',
    ovx: 'auto',
    lh: `${CODE_LINE_HEIGHT}em`,
    d: 'flex',
    ai: 'stretch',
    trs: 'background .3s',
    [`.${codeWrapClass.trim()}:hover &`]: {
        bg: 'var(--code-hover-bg)',
    },
});
const gutterClass = (0, nano_theme_1.rule)({
    flexShrink: 0,
    pd: '16px 12px 18px 18px',
    us: 'none',
    pe: 'none',
    ff: '"JetBrains Mono", "Fira Code", Menlo, monospace',
    fz: '0.9rem',
    lh: '1.7',
    op: 0.3,
    whiteSpace: 'pre',
    ta: 'right',
    bdr: '1px solid rgba(127,127,127,0.22)',
});
const codeClass = (0, nano_theme_1.drule)({
    ...SHARED_TEXT_CSS,
    d: 'block',
    pos: 'relative',
    flex: '1',
    minW: '0',
    '&::selection': {
        bg: 'rgba(0,127,255,0.36)',
        col: 'inherit',
    },
    '&::before': {
        content: '""',
        pos: 'absolute',
        t: '0',
        h: '100%',
        w: '1px',
        bg: 'rgba(127,127,127,0.06)',
        pointerEvents: 'none',
    },
});
const overlayClass = (0, nano_theme_1.rule)({
    ...SHARED_TEXT_CSS,
    pos: 'absolute',
    t: 0,
    l: 0,
    r: 0,
    b: 0,
    pe: 'none',
    us: 'none',
    ov: 'hidden',
    '& *': {
        fontWeight: 'inherit',
        fontStyle: 'inherit',
        padding: 0,
        margin: 0,
        border: 0,
        cursor: 'inherit',
    },
});
const CodeBlock = ({ attributes, children, element }) => {
    const styles = (0, context_1.useStyles)();
    const theme = (0, nano_theme_1.useTheme)();
    const readOnly = (0, slate_react_1.useReadOnly)();
    const stopPointerPropagation = React.useCallback((event) => {
        event.stopPropagation();
    }, []);
    const preventMouseDown = React.useCallback((event) => {
        event.preventDefault();
        event.stopPropagation();
    }, []);
    const codeText = React.useMemo(() => element.children.map(({ text }) => text).join(''), [element.children]);
    const getCodeText = React.useCallback(() => codeText, [codeText]);
    const metaBarStyle = {
        borderBottom: `1px solid ${styles.g(0, styles.light ? 0.06 : 0.1)}`,
        background: theme.bg,
    };
    const lineCount = React.useMemo(() => {
        return (codeText.match(/\n/g)?.length ?? 0) + 1;
    }, [codeText]);
    const lineNumbers = React.useMemo(() => Array.from({ length: lineCount }, (_, i) => String(i + 1)).join('\n'), [lineCount]);
    const languageValue = element.language?.trim() || '';
    const fileNameValue = element.fileName?.trim() || '';
    const showReadOnlyMeta = !!fileNameValue || !!languageValue;
    const wrapColumn = settings.getCodeBlockWrapColumn(element.wrap);
    const showLineNumbers = settings.getCodeBlockShowLineNumbers(element.showLineNumbers);
    const metaLabelMarginLeft = showLineNumbers ? (lineCount > 10 ? 34 : 26) : 0;
    const codeClassName = codeClass({
        textDecorationColor: styles.g(0),
        '&::before': {
            l: `${wrapColumn}ch`,
        },
    });
    const codeStyle = {
        paddingLeft: showLineNumbers ? '14px' : '18px',
        caretColor: styles.g(0),
        color: languageValue ? 'rgba(127,127,127,.1)' : void 0,
    };
    const codeOverlayStyle = {
        paddingLeft: showLineNumbers ? '14px' : '18px',
    };
    const codeContent = (React.createElement("pre", { dir: "ltr", className: codePreClass },
        showLineNumbers ? (React.createElement("div", { contentEditable: false, className: gutterClass, "aria-hidden": "true" }, lineNumbers)) : null,
        React.createElement("code", { className: codeClassName, style: codeStyle },
            !!languageValue && (React.createElement("pre", { className: overlayClass, style: codeOverlayStyle, "aria-hidden": "true", contentEditable: false },
                React.createElement(code_colors_react_1.ColorTokens, { as: "span", code: codeText, lang: languageValue }))),
            React.createElement(ui_1.SetTrace, { name: "isInCodeBlock", value: true }, children))));
    const header = (!readOnly || showReadOnlyMeta) && (React.createElement("div", { contentEditable: false, className: stickyHeaderClass },
        React.createElement("div", { className: metaBarClass, style: metaBarStyle }, readOnly ? (React.createElement("div", { className: metaInputsClass },
            !!fileNameValue && (React.createElement("span", { className: metaLabelClass, style: {
                    opacity: !fileNameValue ? 0.68 : undefined,
                    marginInlineStart: !fileNameValue ? 0 : metaLabelMarginLeft,
                } }, fileNameValue || 'Code block')),
            React.createElement(CopyButton_1.CopyButton, { onCopy: getCodeText, width: 28, height: 28, rounder: true, onMouseDown: preventMouseDown }))) : (
        // biome-ignore lint/a11y/useKeyWithClickEvents: click handler only stops propagation; keyboard interaction lives on inner inputs
        React.createElement("div", { className: metaInputsClass, onMouseDown: stopPointerPropagation, onClick: stopPointerPropagation },
            React.createElement("div", { className: metaPreviewClass },
                React.createElement("span", { className: metaLabelClass, style: {
                        opacity: !fileNameValue ? 0.68 : undefined,
                        marginInlineStart: !fileNameValue ? 0 : metaLabelMarginLeft,
                    } }, fileNameValue || React.createElement(Iconista_1.Iconista, { set: "bootstrap", icon: "file-earmark-code", width: 16, height: 16 }))),
            React.createElement("div", { className: metaActionsClass },
                !!languageValue && React.createElement(Label_1.Label, { className: metaChipClass }, languageValue),
                React.createElement(CopyButton_1.CopyButton, { onCopy: getCodeText, width: 28, height: 28, rounder: true, onMouseDown: preventMouseDown }),
                React.createElement(Popup_1.Popup, { renderContext: () => React.createElement(CodeBlockOptionsPopup_1.CodeBlockOptionsPopup, { element: element }) },
                    React.createElement(BasicButtonMore_1.BasicButtonMore, { type: "button", width: 28, height: 28, rounder: true, tooltip: "Code block options", onMouseDown: preventMouseDown }))))))));
    return (React.createElement("div", { ...attributes, className: codeWrapClass, style: { '--code-hover-bg': styles.g(0, 0.02), textAlign: element.align } },
        React.createElement(Paper_1.default, { round: true, hover: true },
            header,
            codeContent)));
};
exports.CodeBlock = CodeBlock;
